import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminApi, BACKEND_URL } from '../lib/api';
import { uploadImages } from '../lib/uploadImages';
import { slugify } from '../lib/slugify';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, X, Eye, Save, Send,
} from 'lucide-react';
import ArticleContentRenderer from '../components/articles/ArticleContentRenderer';
import GuideContentRenderer from '../components/guides/GuideContentRenderer';

const CONTENT_TYPE_LABELS = { article: 'Article', guide: 'Guide', how_it_works: 'How It Works Topic' };

// New Articles and How-It-Works topics are stored with a fixed slug prefix by existing
// convention: `_how_it_works_summary_from_page_content` (server.py) strips
// "how-it-works-" to derive the public topic_id, and the static-article PDF endpoint
// (GET /articles/static/{slug}/pdf) looks up `page_contents` by `slug=article-{slug}`.
// Guides don't use a prefix (out of scope for the Create New flow).
const SLUG_PREFIXES = { article: 'article-', how_it_works: 'how-it-works-' };
const slugPrefixFor = (contentType) => SLUG_PREFIXES[contentType] || '';

const BLOCK_TYPES = [
  { value: 'paragraph', label: 'Paragraph', factory: () => ({ type: 'paragraph', text: '' }) },
  { value: 'bullet_list', label: 'Bullet List', factory: () => ({ type: 'bullet_list', items: [''] }) },
  { value: 'param_table', label: 'Table', factory: () => ({ type: 'param_table', columns: ['Column 1', 'Column 2'], rows: [['', '']] }) },
  { value: 'step_list', label: 'Step List', factory: () => ({ type: 'step_list', steps: [''] }) },
  { value: 'callout', label: 'Callout', factory: () => ({ type: 'callout', title: '', text: '', tone: 'info' }) },
  { value: 'figure', label: 'Figure (Image)', factory: () => ({ type: 'figure', image_path: null, alt: '', caption: '' }) },
  { value: 'image_gallery', label: 'Image Gallery', factory: () => ({ type: 'image_gallery', images: [] }) },
  { value: 'equation', label: 'Equation', factory: () => ({ type: 'equation', text: '' }) },
  { value: 'link_list', label: 'Link List', factory: () => ({ type: 'link_list', note: '', links: [{ text: '', url: '' }] }) },
];

const blockLabel = (type) => BLOCK_TYPES.find((b) => b.value === type)?.label || type;

const newSection = () => ({
  section_id: crypto.randomUUID(),
  number: '',
  title: '',
  blocks: [],
  subsections: [],
});

const newSubsection = () => ({
  section_id: crypto.randomUUID(),
  number: '',
  title: '',
  blocks: [],
});

// ---------------------------------------------------------------------------
// Block editing
// ---------------------------------------------------------------------------

const ListFieldEditor = ({ items, onChange, placeholder }) => {
  const update = (i, value) => {
    const next = [...items];
    next[i] = value;
    onChange(next);
  };
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={item} placeholder={placeholder} onChange={(e) => update(i, e.target.value)} />
          <Button variant="ghost" size="icon" onClick={() => remove(i)} disabled={items.length <= 1} aria-label="Remove">
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className="gap-1">
        <Plus className="h-3.5 w-3.5" /> Add
      </Button>
    </div>
  );
};

const TableFieldEditor = ({ columns, rows, onChange }) => {
  const updateColumn = (i, value) => {
    const next = [...columns];
    next[i] = value;
    onChange({ columns: next, rows });
  };
  const addColumn = () => onChange({ columns: [...columns, `Column ${columns.length + 1}`], rows: rows.map((r) => [...r, '']) });
  const removeColumn = (i) => onChange({ columns: columns.filter((_, idx) => idx !== i), rows: rows.map((r) => r.filter((_, idx) => idx !== i)) });

  const updateCell = (r, c, value) => {
    const next = rows.map((row) => [...row]);
    next[r][c] = value;
    onChange({ columns, rows: next });
  };
  const addRow = () => onChange({ columns, rows: [...rows, columns.map(() => '')] });
  const removeRow = (r) => onChange({ columns, rows: rows.filter((_, idx) => idx !== r) });

  return (
    <div className="space-y-3 overflow-x-auto">
      <div className="flex items-center gap-2">
        {columns.map((col, i) => (
          <div key={i} className="flex items-center gap-1">
            <Input value={col} onChange={(e) => updateColumn(i, e.target.value)} className="w-32" />
            <Button variant="ghost" size="icon" onClick={() => removeColumn(i)} disabled={columns.length <= 1} aria-label="Remove column">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addColumn} className="gap-1 shrink-0">
          <Plus className="h-3.5 w-3.5" /> Column
        </Button>
      </div>
      {rows.map((row, r) => (
        <div key={r} className="flex items-center gap-2">
          {row.map((cell, c) => (
            <Input key={c} value={cell} onChange={(e) => updateCell(r, c, e.target.value)} className="w-32" />
          ))}
          <Button variant="ghost" size="icon" onClick={() => removeRow(r)} disabled={rows.length <= 1} aria-label="Remove row">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addRow} className="gap-1">
        <Plus className="h-3.5 w-3.5" /> Row
      </Button>
    </div>
  );
};

const LinkListFieldEditor = ({ links, onChange }) => {
  const update = (i, field, value) => {
    const next = links.map((l) => ({ ...l }));
    next[i][field] = value;
    onChange(next);
  };
  const add = () => onChange([...links, { text: '', url: '' }]);
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {links.map((link, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input placeholder="Link text" value={link.text} onChange={(e) => update(i, 'text', e.target.value)} />
          <Input placeholder="https://..." value={link.url} onChange={(e) => update(i, 'url', e.target.value)} />
          <Button variant="ghost" size="icon" onClick={() => remove(i)} disabled={links.length <= 1} aria-label="Remove">
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className="gap-1">
        <Plus className="h-3.5 w-3.5" /> Add Link
      </Button>
    </div>
  );
};

// Shared drag-and-drop dropzone: click-to-browse file input plus an onDrop handler that
// hands dropped files to `onFiles`. Used by the single-image Figure field, the multi-image
// Gallery field, and (via the same pattern) the How-It-Works image fields.
const ImageDropzone = ({ multiple, uploading, label, onFiles }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`rounded-md border-2 border-dashed p-4 text-center transition-colors ${dragOver ? 'border-autoxium-blue bg-autoxium-blue/5' : 'border-structure'}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple={multiple}
        onChange={(e) => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }}
        className="hidden"
      />
      <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="gap-2">
        <ImageIcon className="h-4 w-4" />
        {uploading ? 'Uploading…' : label}
      </Button>
      <p className="text-xs text-muted-foreground mt-1">or drag and drop {multiple ? 'images' : 'an image'} here — PNG, JPG, WEBP, GIF, up to 5MB</p>
    </div>
  );
};

const FigureFieldEditor = ({ block, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    setUploading(true);
    const [uploaded] = await uploadImages(Array.from(files).slice(0, 1), 'content');
    setUploading(false);
    if (uploaded) onChange({ ...block, image_path: uploaded.path });
  };

  const imgSrc = block.image_path
    ? (block.image_path.startsWith('/uploads/') ? `${BACKEND_URL}${block.image_path}` : block.image_path)
    : null;

  return (
    <div className="space-y-3">
      {imgSrc ? (
        <div className="relative w-fit">
          <img src={imgSrc} alt="" className="h-32 w-auto object-cover rounded-sm border border-structure" />
          <button
            type="button"
            onClick={() => onChange({ ...block, image_path: null })}
            className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"
            aria-label="Remove image"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <ImageDropzone multiple={false} uploading={uploading} label="Upload Image" onFiles={handleFiles} />
      )}
      <Input placeholder="Alt text" value={block.alt || ''} onChange={(e) => onChange({ ...block, alt: e.target.value })} />
      <Input placeholder="Caption" value={block.caption || ''} onChange={(e) => onChange({ ...block, caption: e.target.value })} />
    </div>
  );
};

const GalleryFieldEditor = ({ block, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const images = block.images || [];

  const handleFiles = async (files) => {
    setUploading(true);
    const uploaded = await uploadImages(files, 'content');
    setUploading(false);
    if (uploaded.length) {
      onChange({ ...block, images: [...images, ...uploaded.map((u) => ({ path: u.path, alt: '', caption: '' }))] });
    }
  };

  const updateImage = (i, field, value) => {
    const next = images.map((img, idx) => (idx === i ? { ...img, [field]: value } : img));
    onChange({ ...block, images: next });
  };
  const removeImage = (i) => onChange({ ...block, images: images.filter((_, idx) => idx !== i) });
  const moveImage = (i, dir) => {
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ ...block, images: next });
  };

  return (
    <div className="space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {images.map((img, i) => {
            const src = img.path.startsWith('/uploads/') ? `${BACKEND_URL}${img.path}` : img.path;
            return (
              <div key={i} className="rounded-md border border-structure p-2 space-y-2">
                <div className="relative">
                  <img src={src} alt="" className="h-24 w-full object-cover rounded-sm" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"
                    aria-label="Remove image"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
                <Input placeholder="Caption / alt text (optional)" value={img.caption || ''} onChange={(e) => updateImage(i, 'caption', e.target.value)} className="text-xs" />
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === 0} onClick={() => moveImage(i, 'up')} aria-label="Move earlier">
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === images.length - 1} onClick={() => moveImage(i, 'down')} aria-label="Move later">
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ImageDropzone multiple uploading={uploading} label="Upload Images" onFiles={handleFiles} />
    </div>
  );
};

const BlockFields = ({ block, onChange }) => {
  switch (block.type) {
    case 'paragraph':
      return <Textarea rows={3} value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })} placeholder="Paragraph text (supports <b>...</b>)" />;
    case 'bullet_list':
      return <ListFieldEditor items={block.items} onChange={(items) => onChange({ ...block, items })} placeholder="Bullet item" />;
    case 'param_table':
      return <TableFieldEditor columns={block.columns} rows={block.rows} onChange={({ columns, rows }) => onChange({ ...block, columns, rows })} />;
    case 'step_list':
      return <ListFieldEditor items={block.steps} onChange={(steps) => onChange({ ...block, steps })} placeholder="Step" />;
    case 'callout':
      return (
        <div className="space-y-2">
          <Input placeholder="Callout title" value={block.title} onChange={(e) => onChange({ ...block, title: e.target.value })} />
          <Select value={block.tone || 'info'} onValueChange={(tone) => onChange({ ...block, tone })}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="tip">Tip</SelectItem>
            </SelectContent>
          </Select>
          <Textarea rows={2} placeholder="Callout text" value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })} />
        </div>
      );
    case 'figure':
      return <FigureFieldEditor block={block} onChange={onChange} />;
    case 'image_gallery':
      return <GalleryFieldEditor block={block} onChange={onChange} />;
    case 'equation':
      return <Textarea rows={Math.min(12, Math.max(1, (block.text || '').split('\n').length))} className="font-mono" placeholder="Equation text" value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })} />;
    case 'link_list':
      return (
        <div className="space-y-2">
          <Textarea rows={2} placeholder="Note (optional)" value={block.note || ''} onChange={(e) => onChange({ ...block, note: e.target.value })} />
          <LinkListFieldEditor links={block.links} onChange={(links) => onChange({ ...block, links })} />
        </div>
      );
    default:
      return null;
  }
};

const BlockListEditor = ({ blocks, onChange }) => {
  const [addType, setAddType] = useState(BLOCK_TYPES[0].value);

  const updateBlock = (i, block) => {
    const next = [...blocks];
    next[i] = block;
    onChange(next);
  };
  const removeBlock = (i) => onChange(blocks.filter((_, idx) => idx !== i));
  const moveBlock = (i, dir) => {
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const addBlock = () => {
    const factory = BLOCK_TYPES.find((b) => b.value === addType)?.factory;
    if (factory) onChange([...blocks, factory()]);
  };

  return (
    <div className="space-y-3">
      {blocks.map((block, i) => (
        <div key={i} className="rounded-md border border-structure p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{blockLabel(block.type)}</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === 0} onClick={() => moveBlock(i, 'up')} aria-label="Move up">
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === blocks.length - 1} onClick={() => moveBlock(i, 'down')} aria-label="Move down">
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={() => removeBlock(i)} aria-label="Remove block">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <BlockFields block={block} onChange={(b) => updateBlock(i, b)} />
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Select value={addType} onValueChange={setAddType}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {BLOCK_TYPES.map((bt) => (
              <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={addBlock} className="gap-1">
          <Plus className="h-3.5 w-3.5" /> Add Block
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section / Subsection editing
// ---------------------------------------------------------------------------

const SubsectionEditor = ({ subsection, onChange, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) => (
  <div className="rounded-md border border-structure p-4 bg-aluminum/40 space-y-3">
    <div className="flex items-center gap-2">
      <Input
        placeholder="Number (e.g. 5.1)"
        value={subsection.number || ''}
        onChange={(e) => onChange({ ...subsection, number: e.target.value })}
        className="w-32"
      />
      <Input
        placeholder="Subsection title"
        value={subsection.title}
        onChange={(e) => onChange({ ...subsection, title: e.target.value })}
        className="flex-1"
      />
      <Button variant="ghost" size="icon" disabled={!canMoveUp} onClick={onMoveUp} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" disabled={!canMoveDown} onClick={onMoveDown} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={onRemove} aria-label="Remove subsection">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
    <BlockListEditor blocks={subsection.blocks} onChange={(blocks) => onChange({ ...subsection, blocks })} />
  </div>
);

const SectionEditor = ({ section, index, total, onChange, onRemove, onMoveUp, onMoveDown }) => {
  const updateSubsection = (i, sub) => {
    const next = [...section.subsections];
    next[i] = sub;
    onChange({ ...section, subsections: next });
  };
  const removeSubsection = (i) => onChange({ ...section, subsections: section.subsections.filter((_, idx) => idx !== i) });
  const moveSubsection = (i, dir) => {
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= section.subsections.length) return;
    const next = [...section.subsections];
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ ...section, subsections: next });
  };
  const addSubsection = () => onChange({ ...section, subsections: [...section.subsections, newSubsection()] });

  return (
    <Card className="border-2">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Number (e.g. 1)"
            value={section.number || ''}
            onChange={(e) => onChange({ ...section, number: e.target.value })}
            className="w-28"
            data-testid={`section-number-${index}`}
          />
          <Input
            placeholder="Section title"
            value={section.title}
            onChange={(e) => onChange({ ...section, title: e.target.value })}
            className="flex-1"
            data-testid={`section-title-${index}`}
          />
          <Button variant="ghost" size="icon" disabled={index === 0} onClick={onMoveUp} aria-label="Move section up"><ArrowUp className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" disabled={index === total - 1} onClick={onMoveDown} aria-label="Move section down"><ArrowDown className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={onRemove} aria-label="Remove section" data-testid={`remove-section-${index}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <BlockListEditor blocks={section.blocks} onChange={(blocks) => onChange({ ...section, blocks })} />

        {section.subsections.length > 0 && <Separator />}
        <div className="space-y-3">
          {section.subsections.map((sub, i) => (
            <SubsectionEditor
              key={sub.section_id}
              subsection={sub}
              onChange={(s) => updateSubsection(i, s)}
              onRemove={() => removeSubsection(i)}
              onMoveUp={() => moveSubsection(i, 'up')}
              onMoveDown={() => moveSubsection(i, 'down')}
              canMoveUp={i > 0}
              canMoveDown={i < section.subsections.length - 1}
            />
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={addSubsection} className="gap-1">
          <Plus className="h-3.5 w-3.5" /> Add Subsection
        </Button>
      </CardContent>
    </Card>
  );
};

// Single cover/header image for the metadata block — used as the thumbnail on public
// article cards. Same dropzone pattern as FigureFieldEditor, without alt/caption.
const CoverImageField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    setUploading(true);
    const [uploaded] = await uploadImages(Array.from(files).slice(0, 1), 'content');
    setUploading(false);
    if (uploaded) onChange(uploaded.path);
  };

  const imgSrc = value ? (value.startsWith('/uploads/') ? `${BACKEND_URL}${value}` : value) : null;

  return imgSrc ? (
    <div className="relative w-fit mt-1">
      <img src={imgSrc} alt="" className="h-28 w-48 object-cover rounded-sm border border-structure" />
      <button
        type="button"
        onClick={() => onChange(null)}
        className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"
        aria-label="Remove cover image"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  ) : (
    <div className="mt-1">
      <ImageDropzone multiple={false} uploading={uploading} label="Upload Cover Image" onFiles={handleFiles} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const newContent = (contentType) => ({
  slug: slugPrefixFor(contentType),
  content_type: contentType,
  title: '',
  subtitle: '',
  description: '',
  category: '',
  subcategory: '',
  reading_time: '',
  date: new Date().toISOString().slice(0, 10),
  author: '',
  cover_image: null,
  tool_name: '',
  tool_path: '',
  doc_version: '',
  doc_date: '',
  sections: [],
  status: 'DRAFT',
});

const AdminContentEditorPage = () => {
  const { slug: slugParam, contentType: contentTypeParam } = useParams();
  const navigate = useNavigate();
  const isNew = !slugParam;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [content, setContent] = useState(() => (isNew ? newContent(contentTypeParam) : null));
  const [slugTouched, setSlugTouched] = useState(false);
  const [categories, setCategories] = useState([]);

  const fetchContent = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const data = await adminApi.getPageContentAdmin(slugParam);
      setContent(data);
    } catch (err) {
      console.error('Error fetching content:', err);
      toast.error('Failed to load content');
      navigate('/admin/content');
    } finally {
      setLoading(false);
    }
  }, [slugParam, isNew, navigate]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  useEffect(() => {
    adminApi.getPageContentCategories(content?.content_type).then(setCategories).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.content_type]);

  const updateField = (field, value) => setContent((prev) => ({ ...prev, [field]: value }));

  const updateTitle = (value) => {
    setContent((prev) => {
      const next = { ...prev, title: value };
      if (isNew && !slugTouched) {
        next.slug = `${slugPrefixFor(prev.content_type)}${slugify(value)}`;
      }
      return next;
    });
  };

  const updateSlug = (rawValue) => {
    setSlugTouched(true);
    updateField('slug', `${slugPrefixFor(content.content_type)}${slugify(rawValue)}`);
  };

  const updateSection = (i, section) => {
    const next = [...content.sections];
    next[i] = section;
    setContent((prev) => ({ ...prev, sections: next }));
  };
  const removeSection = (i) => setContent((prev) => ({ ...prev, sections: prev.sections.filter((_, idx) => idx !== i) }));
  const moveSection = (i, dir) => {
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= content.sections.length) return;
    const next = [...content.sections];
    [next[i], next[j]] = [next[j], next[i]];
    setContent((prev) => ({ ...prev, sections: next }));
  };
  const addSection = () => setContent((prev) => ({ ...prev, sections: [...prev.sections, newSection()] }));

  const isGuide = content?.content_type === 'guide';

  const buildPayload = () => ({
    title: content.title,
    subtitle: content.subtitle || null,
    description: content.description || null,
    category: content.category || null,
    subcategory: content.subcategory || null,
    reading_time: content.reading_time || null,
    date: content.date || null,
    author: content.author || null,
    cover_image: content.cover_image || null,
    tool_name: content.tool_name || null,
    tool_path: content.tool_path || null,
    doc_version: content.doc_version || null,
    doc_date: content.doc_date || null,
    sections: content.sections,
  });

  const handleSave = async (publish) => {
    if (!content.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (isNew) {
      const prefix = slugPrefixFor(content.content_type);
      const slugBody = content.slug.slice(prefix.length);
      if (!slugBody || !/^[a-z0-9-]+$/.test(content.slug)) {
        toast.error('Slug must contain only lowercase letters, numbers, and hyphens');
        return;
      }
    }
    if (publish && content.sections.length === 0) {
      toast.error('Add at least one content section before publishing');
      return;
    }
    const setBusy = publish ? setPublishing : setSaving;
    setBusy(true);
    try {
      const payload = { ...buildPayload(), status: publish ? 'PUBLISHED' : 'DRAFT' };
      if (isNew) {
        const created = await adminApi.createPageContent({ ...payload, slug: content.slug, content_type: content.content_type });
        toast.success(publish ? 'Published' : 'Draft created');
        navigate(`/admin/content/${created.slug}`, { replace: true });
      } else {
        const updated = await adminApi.updatePageContent(slugParam, payload);
        setContent(updated);
        toast.success(publish ? 'Published' : 'Draft saved');
      }
    } catch (err) {
      console.error('Error saving content:', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !content) {
    return (
      <div className="min-h-[calc(100vh-4rem)] py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="h-8 w-1/2 bg-muted rounded animate-pulse" />
          <div className="h-32 w-full bg-muted rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-content-editor-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <Link to="/admin/content">
            <Button variant="ghost" size="icon" data-testid="back-to-content-library-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1 min-w-[200px]">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight truncate">
              {isNew ? `New ${CONTENT_TYPE_LABELS[content.content_type] || 'Content'}` : (content.title || 'Untitled')}
            </h1>
            {isNew ? (
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {slugPrefixFor(content.content_type) && (
                  <span className="text-muted-foreground text-sm font-mono">{slugPrefixFor(content.content_type)}</span>
                )}
                <Input
                  value={content.slug.slice(slugPrefixFor(content.content_type).length)}
                  onChange={(e) => updateSlug(e.target.value)}
                  placeholder="url-slug"
                  className="h-7 font-mono text-sm max-w-xs"
                  data-testid="ce-slug-input"
                />
                <span className="text-muted-foreground text-sm font-mono">· {content.status}</span>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm font-mono">{content.slug} · {content.status}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setShowPreview(true)} className="gap-2" data-testid="preview-content-btn">
              <Eye className="h-4 w-4" /> Preview
            </Button>
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving || publishing} className="gap-2" data-testid="save-draft-btn">
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button onClick={() => handleSave(true)} disabled={saving || publishing} className="gap-2" data-testid="publish-content-btn">
              <Send className="h-4 w-4" /> {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </div>
        </div>

        <Card className="border-2 mb-6">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label htmlFor="ce-title">Title *</Label>
              <Input id="ce-title" value={content.title} onChange={(e) => updateTitle(e.target.value)} />
            </div>

            <div>
              <Label htmlFor="ce-cover-image">Cover Image</Label>
              <CoverImageField value={content.cover_image} onChange={(path) => updateField('cover_image', path)} />
            </div>

            {isGuide ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ce-tool-name">Tool Name</Label>
                  <Input id="ce-tool-name" value={content.tool_name || ''} onChange={(e) => updateField('tool_name', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ce-tool-path">Tool Path</Label>
                  <Input id="ce-tool-path" placeholder="/tools/..." value={content.tool_path || ''} onChange={(e) => updateField('tool_path', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ce-doc-version">Document Version</Label>
                  <Input id="ce-doc-version" value={content.doc_version || ''} onChange={(e) => updateField('doc_version', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ce-doc-date">Document Date</Label>
                  <Input id="ce-doc-date" value={content.doc_date || ''} onChange={(e) => updateField('doc_date', e.target.value)} />
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="ce-subtitle">Subtitle</Label>
                  <Input id="ce-subtitle" value={content.subtitle || ''} onChange={(e) => updateField('subtitle', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ce-description">Description</Label>
                  <Input id="ce-description" value={content.description || ''} onChange={(e) => updateField('description', e.target.value)} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="ce-category">Category</Label>
                    <Input id="ce-category" list="ce-category-suggestions" value={content.category || ''} onChange={(e) => updateField('category', e.target.value)} />
                    <datalist id="ce-category-suggestions">
                      {categories.map((c) => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                  <div>
                    <Label htmlFor="ce-subcategory">Subcategory</Label>
                    <Input id="ce-subcategory" placeholder="Optional — e.g. Longitudinal ADAS" value={content.subcategory || ''} onChange={(e) => updateField('subcategory', e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="ce-date">Date</Label>
                    <Input id="ce-date" value={content.date || ''} onChange={(e) => updateField('date', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ce-reading-time">Reading Time</Label>
                    <Input id="ce-reading-time" value={content.reading_time || ''} onChange={(e) => updateField('reading_time', e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="ce-author">Author</Label>
                    <Input id="ce-author" value={content.author || ''} onChange={(e) => updateField('author', e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {content.sections.map((section, i) => (
            <SectionEditor
              key={section.section_id}
              section={section}
              index={i}
              total={content.sections.length}
              onChange={(s) => updateSection(i, s)}
              onRemove={() => removeSection(i)}
              onMoveUp={() => moveSection(i, 'up')}
              onMoveDown={() => moveSection(i, 'down')}
            />
          ))}
        </div>

        <Button variant="outline" onClick={addSection} className="gap-2 mt-4" data-testid="add-section-btn">
          <Plus className="h-4 w-4" /> Add Section
        </Button>
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[95vw] w-full max-h-[92vh] overflow-y-auto p-0">
          {isGuide ? (
            <GuideContentRenderer content={content} />
          ) : (
            <ArticleContentRenderer content={content} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContentEditorPage;
